import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import Pessoa, { VinculoNome } from '../../../model/Pessoa';
import MembroBanca from '../../../model/banca/MembroBanca';
import BackendErrorMessage from '../../../components/BackendErrorMessage';
import api from '../../../api';
import AnimatedModal from './AnimatedModal';
import ModalHelp from '../../../components/ModalHelp';

interface EmergenciaModalProps {
  banca: any;
  onClose: () => void;
  onSaveSuccess: () => void;
}

const EmergenciaModal: React.FC<EmergenciaModalProps> = ({ 
  banca, 
  onClose, 
  onSaveSuccess 
}) => {
  const { processoId } = useParams<{ processoId: string }>();
  const [selectedMembroBanca, setSelectedMembroBanca] = useState<MembroBanca | null>(null);
  const [selectedPessoa, setSelectedPessoa] = useState<Pessoa | null>(null);
  const [pessoasDisponiveis, setPessoasDisponiveis] = useState<Pessoa[]>([]);
  const [membrosDefinitivos, setMembrosDefinitivos] = useState<MembroBanca[]>([]);
  const [filter, setFilter] = useState('');
  const [vinculoFilter, setVinculoFilter] = useState<VinculoNome | ''>('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchPessoasEmergencia = async () => {
      try {
        const response = await api.get(`/bancas/${banca.id}/pessoas-emergencia`);
        const { membrosDefinitivos, pessoasDisponiveis } = response.data;
        setMembrosDefinitivos(membrosDefinitivos);
        setPessoasDisponiveis(pessoasDisponiveis);
      } catch (error) {
        console.error("Erro ao carregar dados de emergência", error);
        toast.error(BackendErrorMessage(error));
      }
    };

    fetchPessoasEmergencia();
  }, [banca.id]);

  const filteredPessoasDisponiveis = pessoasDisponiveis?.filter(pessoa =>
    pessoa.nome.toLowerCase().includes(filter.toLowerCase()) &&
    (vinculoFilter === '' || pessoa.vinculo.nome === vinculoFilter)
  );

  useEffect(() => {
    if (filteredPessoasDisponiveis.length > 0) {
      setSelectedPessoa(filteredPessoasDisponiveis[0]);
    } else {
      setSelectedPessoa(null);
    }
  }, [filter, vinculoFilter, pessoasDisponiveis]);

  const handleSave = async () => {
    if (!selectedMembroBanca || !selectedPessoa) {
      toast.error('Selecione um membro para remover e uma pessoa para adicionar.');
      return;
    }

    if (!selectedMembroBanca.id || !selectedPessoa.id) {
      toast.error('IDs inválidos. Por favor, tente novamente.');
      return;
    }

    setLoading(true);
    try {
      await api.patch(
        `/processos/${processoId}/bancas/${banca.id}/membros/substituicao`,
        {
          membroId: selectedMembroBanca.id,
          pessoaId: selectedPessoa.id
        }
      );
      
      toast.success('Membro substituído com sucesso!');
      onSaveSuccess();
      onClose();
    } catch (error) {
      console.error('Erro ao substituir membro:', error);
      toast.error(BackendErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <AnimatedModal isOpen={true} onClose={onClose}>
      <div style={{
        backgroundColor: 'white',
        padding: '30px',
        borderRadius: '8px',
        width: '600px',
        maxWidth: '90vw',
        maxHeight: '80vh',
        overflow: 'auto'
      }}>
        <h3 style={{ marginBottom: '20px', textAlign: 'center' }}>
          Substituição de Membro de Banca
        <ModalHelp text="Selecione o membro a ser removido e escolha o substituto. O novo membro receberá um email de notificação automaticamente. Docentes só podem ser substituídos por outros docentes." />
        </h3>
        
        {/* Seção Remover Membro */}
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '20px',
          borderRadius: '6px',
          marginBottom: '20px',
          border: '1px solid #e9ecef'
        }}>
          <h4 style={{ marginBottom: '15px', fontSize: '16px', fontWeight: '600' }}>
            Remover Membro da Banca
          </h4>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              Selecione um Membro:
            </label>
            <select
              value={selectedMembroBanca?.id || ''}
              onChange={(e) => {
                const membro = membrosDefinitivos.find(m => m.id === e.target.value);
                setSelectedMembroBanca(membro || null);
              }}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            >
              <option value="">Selecione...</option>
              {membrosDefinitivos.map(membro => (
                <option key={membro.id} value={membro.id ?? ''}>
                  {membro.pessoa.nome} ({membro.papelMembro.nome})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Seção Adicionar Membro */}
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '20px',
          borderRadius: '6px',
          marginBottom: '20px',
          border: '1px solid #e9ecef'
        }}>
          <h4 style={{ marginBottom: '15px', fontSize: '16px', fontWeight: '600' }}>
            Adicionar Membro à Banca
          </h4>
          
          {/* Filtros */}
          <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#6c757d' }}>
                Filtrar por Nome:
              </label>
              <input
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Digite o nome da pessoa..."
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#6c757d' }}>
                Filtrar por Vínculo:
              </label>
              <select
                value={vinculoFilter}
                onChange={(e) => setVinculoFilter(e.target.value as VinculoNome)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              >
                <option value="">Todos os Vínculos</option>
                {Object.values(VinculoNome).map(vinculo => (
                  <option key={vinculo} value={vinculo}>
                    {vinculo}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Seleção de Pessoa */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              Selecione um Membro:
            </label>
            <select
              value={selectedPessoa?.id || ''}
              onChange={(e) => {
                const pessoa = pessoasDisponiveis.find(p => p.id === e.target.value);
                setSelectedPessoa(pessoa || null);
              }}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            >
              <option value="">Selecione...</option>
              {filteredPessoasDisponiveis.map(pessoa => (
                <option key={pessoa.id} value={pessoa.id}>
                  {pessoa.nome} ({pessoa.vinculo.nome})
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '8px 16px',
              border: '1px solid #ddd',
              background: 'white',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            style={{
              padding: '8px 16px',
              backgroundColor: '#28a745',
              color: 'white',
              border: '1px solid #28a745',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
            disabled={loading || !selectedMembroBanca || !selectedPessoa}
          >
            {loading ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </AnimatedModal>
  );
};

export default EmergenciaModal;